<div class="min-h-screen bg-gray-50">
  <!-- Skeleton Loading State -->
  <app-property-detail-skeleton *ngIf="loading()"></app-property-detail-skeleton>

  <!-- Error State -->
  <div *ngIf="error() && !loading()" class="flex justify-center items-center min-h-screen">
    <div class="text-center">
      <h2 class="text-2xl font-bold text-gray-900 mb-4">{{ error() }}</h2>
      <button 
        (click)="goBack()"
        aria-label="Go back to previous page"
        class="px-6 py-2 bg-immovea-navy text-white rounded-lg hover:bg-blue-900 transition">
        Go Back
      </button>
    </div>
  </div>

  <!-- Property Detail -->
  <div *ngIf="property() && !loading()">
    <!-- Top Navigation Bar -->
    <div class="bg-white border-b sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-4 flex items-center justify-between">
        <h1 class="text-lg sm:text-2xl font-bold text-gray-900 line-clamp-1">{{ property()?.title }}</h1>
        <div class="flex items-center gap-2 sm:gap-3">
          <button 
            (click)="toggleFavorite()"
            aria-label="Toggle favorite"
            class="p-2 sm:p-2.5 rounded-full hover:bg-gray-100 transition-all duration-200 hover:scale-110"
            [class.bg-red-50]="isFavorite()">
            <svg 
              class="w-5 h-5 sm:w-6 sm:h-6 transition-colors"
              [class.fill-red-500]="isFavorite()"
              [class.text-red-500]="isFavorite()"
              [class.text-gray-400]="!isFavorite()"
              [attr.fill]="isFavorite() ? 'currentColor' : 'none'"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
          <button 
            (click)="goBack()"
            aria-label="Navigate back"
            class="flex items-center gap-2 px-3 sm:px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition text-sm sm:text-base">
            ← Back
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <!-- Image Gallery - Left Column -->
        <div class="lg:col-span-2 space-y-4">
          <!-- Main Image -->
          <div class="bg-white rounded-xl overflow-hidden shadow-lg relative group cursor-pointer" (click)="openLightbox()">
            <div class="bg-gray-300 aspect-video flex items-center justify-center relative">
              <img 
                *ngIf="currentImageUrl()"
                [src]="currentImageUrl()" 
                [alt]="property()?.title || 'Property image'"
                class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105">
              <div *ngIf="!currentImageUrl()" class="text-gray-500 text-lg">No image available</div>
              
              <!-- Click to Enlarge Overlay -->
              <div *ngIf="currentImageUrl()" class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                <div class="bg-white bg-opacity-90 px-4 py-2 rounded-full font-semibold text-gray-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 shadow-lg">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
                  </svg>
                  Click to Enlarge
                </div>
              </div>
            </div>

            <!-- Featured Badge -->
            <div class="absolute top-4 right-4 bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1" *ngIf="property()?.add_to_homepage">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Featured
            </div>

            <!-- Image Counter -->
            <div class="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm font-medium" *ngIf="images().length > 1">
              {{ selectedImageIndex() + 1 }} / {{ images().length }}
            </div>
          </div>

          <!-- Image Navigation -->
          <div *ngIf="images().length > 1" class="flex gap-2">
            <button 
              (click)="previousImage()"
              aria-label="View previous property image"
              class="flex-1 px-4 py-3 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium border border-gray-200">
              ← Previous
            </button>
            <button 
              (click)="nextImage()"
              aria-label="View next property image"
              class="flex-1 px-4 py-3 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium border border-gray-200">
              Next →
            </button>
          </div>

          <!-- Image Thumbnails -->
          <div *ngIf="images().length > 1" class="flex gap-2 overflow-x-auto pb-2">
            <button 
              *ngFor="let img of images(); let i = index"
              (click)="selectImage(i)"
              [attr.aria-label]="'View image ' + (i + 1) + ' of ' + images().length"
              [class.ring-2]="selectedImageIndex() === i"
              class="flex-shrink-0 w-20 h-20 bg-gray-300 rounded-lg overflow-hidden hover:opacity-80 transition ring-immovea-navy cursor-pointer">
              <img 
                [src]="getThumbnailUrl(img.storage_path)"
                [alt]="img.alt_text || 'Property thumbnail'"
                class="w-full h-full object-cover">
            </button>
          </div>

          <!-- Lightbox Modal -->
          <div *ngIf="isLightboxOpen()" 
               class="fixed inset-0 z-[9999] bg-black bg-opacity-95 flex items-center justify-center animate-in fade-in duration-200"
               (click)="closeLightbox()">
            <!-- Close Button -->
            <button 
              (click)="closeLightbox()"
              aria-label="Close image lightbox"
              class="absolute top-4 right-4 w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white transition-all z-10 backdrop-blur-sm">
              <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>

            <!-- Image Counter -->
            <div *ngIf="images().length > 1" class="absolute top-6 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-6 py-3 rounded-full text-lg font-semibold backdrop-blur-sm z-10">
              {{ selectedImageIndex() + 1 }} / {{ images().length }}
            </div>

            <!-- Previous Button -->
            <button 
              *ngIf="images().length > 1"
              (click)="previousImageLightbox(); $event.stopPropagation()"
              aria-label="Previous image in lightbox"
              class="absolute left-4 w-14 h-14 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white transition-all backdrop-blur-sm z-10">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>

            <!-- Main Image -->
            <div class="max-w-7xl max-h-[90vh] mx-4" (click)="$event.stopPropagation()">
              <img 
                [src]="currentImageUrl()"
                [alt]="property()?.title || 'Property image'"
                class="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl">
            </div>

            <!-- Next Button -->
            <button 
              *ngIf="images().length > 1"
              (click)="nextImageLightbox(); $event.stopPropagation()"
              aria-label="Next image in lightbox"
              class="absolute right-4 w-14 h-14 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white transition-all backdrop-blur-sm z-10">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>

            <!-- Instructions -->
            <div class="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-6 py-3 rounded-full text-sm backdrop-blur-sm">
              <span class="hidden sm:inline">Press ESC to close • Use arrow keys to navigate</span>
              <span class="sm:hidden">Tap outside to close</span>
            </div>
          </div>

          <!-- Description -->
          <div class="bg-white rounded-lg sm:rounded-xl p-6 sm:p-8 shadow mt-6">
            <h3 class="text-lg sm:text-xl font-bold text-gray-900 mb-4">About this property</h3>
            <div 
              class="description-content"
              [innerHTML]="property()?.description || '<p class=\'text-gray-400 italic\'>No description available</p>' | safeHtml"
            ></div>
          </div>

          <!-- Building Information -->
          <div *ngIf="property()?.year_built || property()?.building_standing || property()?.building_floors || property()?.has_elevator || property()?.has_generator || property()?.neighborhood || property()?.zone" 
               class="bg-white rounded-lg sm:rounded-xl p-6 sm:p-8 shadow mt-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
              </div>
              <h3 class="text-lg sm:text-xl font-bold text-gray-900">Building Information</h3>
            </div>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div *ngIf="property()?.year_built" class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Year Built</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.year_built }}</p>
              </div>
              <div *ngIf="property()?.building_floors" class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Building Floors</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.building_floors }}</p>
              </div>
              <div *ngIf="property()?.apartments_per_floor" class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Apts/Floor</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.apartments_per_floor }}</p>
              </div>
              <div *ngIf="property()?.neighborhood" class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Neighborhood</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.neighborhood }}</p>
              </div>
              <div *ngIf="property()?.zone" class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Zone</p>
                <p class="text-lg font-bold text-gray-900 capitalize">{{ property()?.zone }}</p>
              </div>
              <div *ngIf="property()?.building_standing" class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 col-span-2 md:col-span-1">
                <p class="text-sm text-gray-600 mb-1">Building Standing</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.building_standing }}</p>
              </div>
            </div>
            <div *ngIf="property()?.has_elevator || property()?.has_generator" class="flex flex-wrap gap-2 mt-4">
              <span *ngIf="property()?.has_elevator" class="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold flex items-center gap-1.5">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Elevator
              </span>
              <span *ngIf="property()?.has_generator" class="px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold flex items-center gap-1.5">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Generator
              </span>
            </div>
          </div>

          <!-- Area & Dimensions -->
          <div *ngIf="property()?.habitable_area_m2 || property()?.land_area_m2 || property()?.usable_area_m2 || property()?.total_area_m2 || property()?.ceiling_height_m" 
               class="bg-white rounded-lg sm:rounded-xl p-6 sm:p-8 shadow mt-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
                </svg>
              </div>
              <h3 class="text-lg sm:text-xl font-bold text-gray-900">Area & Dimensions</h3>
            </div>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div *ngIf="property()?.habitable_area_m2" class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Habitable Area</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.habitable_area_m2 }} m²</p>
              </div>
              <div *ngIf="property()?.land_area_m2" class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Land Area</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.land_area_m2 }} m²</p>
              </div>
              <div *ngIf="property()?.usable_area_m2" class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Usable Area</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.usable_area_m2 }} m²</p>
              </div>
              <div *ngIf="property()?.total_area_m2" class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Total Area</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.total_area_m2 }} m²</p>
              </div>
              <div *ngIf="property()?.ceiling_height_m" class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                <p class="text-sm text-gray-600 mb-1">Ceiling Height</p>
                <p class="text-lg font-bold text-gray-900">{{ property()?.ceiling_height_m }} m</p>
              </div>
            </div>
          </div>

          <!-- Room Configuration -->
          <div *ngIf="property()?.living_room || property()?.living_dining || property()?.master_bedrooms || property()?.guest_bathrooms || property()?.maid_bathrooms || property()?.maid_rooms || property()?.office_rooms" 
               class="bg-white rounded-lg sm:rounded-xl p-6 sm:p-8 shadow mt-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
              </div>
              <h3 class="text-lg sm:text-xl font-bold text-gray-900">Room Configuration</h3>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              <div *ngIf="property()?.living_room" class="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center">
                <div class="text-2xl font-bold text-indigo-600 mb-1">{{ property()?.living_room }}</div>
                <p class="text-sm text-gray-600">Living Room{{ (property()?.living_room || 0) > 1 ? 's' : '' }}</p>
              </div>
              <div *ngIf="property()?.living_dining" class="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center">
                <div class="text-2xl font-bold text-indigo-600 mb-1">{{ property()?.living_dining }}</div>
                <p class="text-sm text-gray-600">Living/Dining</p>
              </div>
              <div *ngIf="property()?.master_bedrooms" class="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center">
                <div class="text-2xl font-bold text-indigo-600 mb-1">{{ property()?.master_bedrooms }}</div>
                <p class="text-sm text-gray-600">Master Bedroom{{ (property()?.master_bedrooms || 0) > 1 ? 's' : '' }}</p>
              </div>
              <div *ngIf="property()?.guest_bathrooms" class="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center">
                <div class="text-2xl font-bold text-indigo-600 mb-1">{{ property()?.guest_bathrooms }}</div>
                <p class="text-sm text-gray-600">Guest Bath{{ (property()?.guest_bathrooms || 0) > 1 ? 's' : '' }}</p>
              </div>
              <div *ngIf="property()?.maid_bathrooms" class="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center">
                <div class="text-2xl font-bold text-indigo-600 mb-1">{{ property()?.maid_bathrooms }}</div>
                <p class="text-sm text-gray-600">Maid Bath{{ (property()?.maid_bathrooms || 0) > 1 ? 's' : '' }}</p>
              </div>
              <div *ngIf="property()?.maid_rooms" class="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center">
                <div class="text-2xl font-bold text-indigo-600 mb-1">{{ property()?.maid_rooms }}</div>
                <p class="text-sm text-gray-600">Maid Room{{ (property()?.maid_rooms || 0) > 1 ? 's' : '' }}</p>
              </div>
              <div *ngIf="property()?.office_rooms" class="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center">
                <div class="text-2xl font-bold text-indigo-600 mb-1">{{ property()?.office_rooms }}</div>
                <p class="text-sm text-gray-600">Office{{ (property()?.office_rooms || 0) > 1 ? 's' : '' }}</p>
              </div>
            </div>
          </div>

          <!-- Property Features -->
          <div *ngIf="property()?.is_duplex || property()?.has_terrace || property()?.has_garden || property()?.has_view || property()?.ground_floor || property()?.mezzanine || property()?.basement" 
               class="bg-white rounded-lg sm:rounded-xl p-6 sm:p-8 shadow mt-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                </svg>
              </div>
              <h3 class="text-lg sm:text-xl font-bold text-gray-900">Property Features</h3>
            </div>
            <div class="flex flex-wrap gap-2">
              <span *ngIf="property()?.is_duplex" class="px-4 py-2 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Duplex
              </span>
              <span *ngIf="property()?.has_terrace" class="px-4 py-2 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Terrace
              </span>
              <span *ngIf="property()?.has_garden" class="px-4 py-2 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Garden
              </span>
              <span *ngIf="property()?.has_view" class="px-4 py-2 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                View
              </span>
              <span *ngIf="property()?.ground_floor" class="px-4 py-2 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Ground Floor
              </span>
              <span *ngIf="property()?.mezzanine" class="px-4 py-2 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Mezzanine
              </span>
              <span *ngIf="property()?.basement" class="px-4 py-2 bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Basement
              </span>
            </div>
          </div>

          <!-- Amenities & Equipment -->
          <div *ngIf="property()?.anti_seismic || property()?.ac_split || property()?.ac_central || property()?.oil_heating || property()?.fireplace || property()?.american_kitchen || property()?.fitted_kitchen || property()?.kitchenette || property()?.double_glazing || property()?.electric_shutters || property()?.concierge || property()?.videophone || property()?.is_furnished" 
               class="bg-white rounded-lg sm:rounded-xl p-6 sm:p-8 shadow mt-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <h3 class="text-lg sm:text-xl font-bold text-gray-900">Amenities & Equipment</h3>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              <div *ngIf="property()?.anti_seismic" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Anti-Seismic</span>
              </div>
              <div *ngIf="property()?.ac_split" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Split AC</span>
              </div>
              <div *ngIf="property()?.ac_central" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Central AC</span>
              </div>
              <div *ngIf="property()?.oil_heating" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Oil Heating</span>
              </div>
              <div *ngIf="property()?.fireplace" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Fireplace</span>
              </div>
              <div *ngIf="property()?.american_kitchen" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">American Kitchen</span>
              </div>
              <div *ngIf="property()?.fitted_kitchen" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Fitted Kitchen</span>
              </div>
              <div *ngIf="property()?.kitchenette" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Kitchenette</span>
              </div>
              <div *ngIf="property()?.double_glazing" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Double Glazing</span>
              </div>
              <div *ngIf="property()?.electric_shutters" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Electric Shutters</span>
              </div>
              <div *ngIf="property()?.concierge" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Concierge</span>
              </div>
              <div *ngIf="property()?.videophone" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Videophone</span>
              </div>
              <div *ngIf="property()?.is_furnished" class="flex items-center gap-2 bg-cyan-50 rounded-lg p-3">
                <svg class="w-5 h-5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Furnished</span>
              </div>
            </div>
          </div>

          <!-- Condition & Parking -->
          <div *ngIf="property()?.general_condition || property()?.needs_renovation || property()?.parking_covered || property()?.parking_outdoor || property()?.cellar" 
               class="bg-white rounded-lg sm:rounded-xl p-6 sm:p-8 shadow mt-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 class="text-lg sm:text-xl font-bold text-gray-900">Condition & Parking</h3>
            </div>
            <div class="space-y-4">
              <div *ngIf="property()?.general_condition" class="flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
                <span class="text-sm font-medium text-gray-700">General Condition</span>
                <span class="px-3 py-1 bg-white rounded-full text-sm font-bold text-gray-900 capitalize">
                  {{ property()?.general_condition?.replace('_', ' ') }}
                </span>
              </div>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div *ngIf="property()?.parking_covered" class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                  <p class="text-sm text-gray-600 mb-1">Covered Parking</p>
                  <p class="text-2xl font-bold text-gray-900">{{ property()?.parking_covered }}</p>
                </div>
                <div *ngIf="property()?.parking_outdoor" class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                  <p class="text-sm text-gray-600 mb-1">Outdoor Parking</p>
                  <p class="text-2xl font-bold text-gray-900">{{ property()?.parking_outdoor }}</p>
                </div>
              </div>
              <div *ngIf="property()?.needs_renovation || property()?.cellar" class="flex flex-wrap gap-2">
                <span *ngIf="property()?.needs_renovation" class="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm font-semibold flex items-center gap-2">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                  </svg>
                  Needs Renovation
                </span>
                <span *ngIf="property()?.cellar" class="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-semibold flex items-center gap-2">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  </svg>
                  Cellar
                </span>
              </div>
            </div>
          </div>

          <!-- Map Section -->
          <div *ngIf="property()?.google_map_url" class="bg-white rounded-lg sm:rounded-xl p-6 sm:p-8 shadow mt-6">
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </div>
              <h3 class="text-lg sm:text-xl font-bold text-gray-900">Location Map</h3>
            </div>
            <a [href]="property()?.google_map_url" target="_blank" rel="noopener noreferrer"
               class="flex items-center justify-center gap-2 w-full px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition font-semibold shadow-md hover:shadow-lg">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
              </svg>
              View on Google Maps
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
              </svg>
            </a>
          </div>
        </div>

        <!-- Sidebar - Right Column -->
        <div class="lg:col-span-1">
          <!-- Price Card -->
          <div class="bg-gradient-to-br from-immovea-navy to-blue-900 rounded-lg sm:rounded-xl p-6 sm:p-8 text-white shadow-lg lg:sticky lg:top-24">
            <p class="text-blue-100 mb-1 sm:mb-2 text-sm">Price</p>
            <p class="text-3xl sm:text-5xl font-bold mb-1 sm:mb-2">
              {{ propertyService.formatPrice(property()?.price || 0, property()?.currency || 'USD') }}
            </p>
            <p class="text-blue-100 mb-4 sm:mb-6 text-sm">
              {{ property()?.listing_type === 'sale' ? 'For Sale' : 'For Rent' }}
            </p>

            <!-- Quick Details -->
            <div class="space-y-2 sm:space-y-4 pt-4 sm:pt-6 border-t border-blue-400">
              <div class="flex justify-between items-center text-sm sm:text-base">
                <span class="text-blue-100">Property Type</span>
                <span class="font-semibold">{{ property()?.property_type }}</span>
              </div>
              <div *ngIf="property()?.floor" class="flex justify-between items-center text-sm sm:text-base">
                <span class="text-blue-100">Floor</span>
                <span class="font-semibold">{{ property()?.floor }}</span>
              </div>
              <div *ngIf="property()?.year_built" class="flex justify-between items-center text-sm sm:text-base">
                <span class="text-blue-100">Built</span>
                <span class="font-semibold">{{ property()?.year_built }}</span>
              </div>
              <div *ngIf="property()?.neighborhood" class="flex justify-between items-center text-sm sm:text-base">
                <span class="text-blue-100">Neighborhood</span>
                <span class="font-semibold">{{ property()?.neighborhood }}</span>
              </div>
              <div class="flex justify-between items-start text-sm sm:text-base">
                <span class="text-blue-100">Location</span>
                <span class="font-semibold text-right max-w-[60%]">
                  <span *ngIf="property()?.address_line1">{{ property()?.address_line1 }}<br></span>
                  <span *ngIf="property()?.address_line2">{{ property()?.address_line2 }}<br></span>
                  {{ property()?.city }}<span *ngIf="property()?.state">, {{ property()?.state }}</span><span *ngIf="property()?.postal_code"> {{ property()?.postal_code }}</span><br>
                  {{ property()?.country }}
                </span>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="space-y-2 sm:space-y-3 mt-6 sm:mt-8">
              <button 
                aria-label="Contact about this property"
                class="w-full px-4 py-2 sm:py-3 bg-white text-immovea-navy rounded-lg font-bold hover:bg-gray-100 transition flex items-center justify-center gap-2 text-sm sm:text-base">
                <svg class="w-4 sm:w-5 h-4 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773c.418 1.59 1.614 2.38 3.186 3.954 1.574 1.574 2.365 2.768 3.954 3.186l.773-1.548a1 1 0 011.06-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2.57C9.016 17 3 10.984 3 3.43V.5a1 1 0 01-1-1h2z" />
                </svg>
                Call Us
              </button>
              <a 
                [routerLink]="['/contact']"
                [queryParams]="{ subject: property()?.title }"
                aria-label="Email us about this property"
                class="w-full px-4 py-2 sm:py-3 bg-emerald-500 text-white rounded-lg font-bold hover:bg-emerald-600 transition flex items-center justify-center gap-2 text-sm sm:text-base">
                <svg class="w-4 sm:w-5 h-4 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
